import { useEffect, useState } from "react"
import { ButtonPrimary, ButtonSecondary } from "@nova/component-btn"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { formatBytes } from "@nova/format"
import * as Icons from "@nova/component-icons"
import classNames from "classnames"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import type { AppDispatch } from "integrations/redux/store"
import { useUploadFlowContext } from "./useUploadFlowContext"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import {
    selectNonArchivedOrthoFlights,
    selectNonArchivedInfraredFlights,
    selectOrthoFlightById,
    selectInfraredFlightById,
} from "features/entity-caching/flights/selectors"
import { selectAllEntitiesMap } from "panels/content/side-panel/slice"
import { getAncestors } from "panels/content/side-panel/utils"
import { compareFunctionsBySortMode } from "panels/content/side-panel/constants"
import { selectSortLayersValue } from "panels/content/side-panel/slice.new"
import { NovaPluginIds } from "panels/controls/PluginAccessPanel/constants"
import { WorkspaceCompactTextInput } from "@nova/component-inline-text-input"
import {
    stageRemote as stageOrthoRemote,
    updateManyUploads as updateManyOrthoUploads,
    selectUploadsForSet as selectOrthoUploadsForSet,
    OrthoState,
} from "plugins/Orthomosaic/OrthomosaicSlice"
import {
    stageRemote as stageInfraredRemote,
    updateManyUploads as updateManyInfraredUploads,
    selectUploadsForSet as selectInfraredUploadsForSet,
    InfraredImageState,
} from "plugins/InfraredImage/InfraredImageSlice"
import { OrthoFlight, InfraredFlight, OrthoImage, InfraredImage } from "@nova/protobuf"
import type { SidePanelLayerOrGroup } from "@nova/constants"
import { ImageProcessingState, isUsable } from "@nova/constants"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { ingestEventEmitter } from "features/data-management/files/fileManagementApi"
import type { IngestResponse } from "@nova/schemas"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"

// Color Finder is intentionally excluded for now (see the matching note in the
// import-images api-gateway route).
type FlightType = "ortho" | "infrared"

type OrthoSourceFlight = {
    id: string
    pathLabel: string
    type: "ortho"
    imageCount: number
    estimatedBytes: number
    images: OrthoImage[]
}
type InfraredSourceFlight = {
    id: string
    pathLabel: string
    type: "infrared"
    imageCount: number
    estimatedBytes: number
    images: InfraredImage[]
}
type SourceFlight = OrthoSourceFlight | InfraredSourceFlight

type StagedEntry = OrthoState | InfraredImageState

const FLIGHT_TYPE_LABEL: Record<FlightType, string> = {
    ortho: "Orthomosaic",
    infrared: "Hotspots",
}

// Match the PrimaryIcon each plugin registers, so rows read like the side panel.
const FLIGHT_TYPE_ICON: Record<FlightType, Icons.IconComponent> = {
    ortho: Icons.HotspotOrthoIcon,
    infrared: Icons.HotspotPoint,
}

/** The flight type a given plugin can load images into, or null if unsupported. */
export function loadFromLayerTargetType(pluginId: NovaPluginIds): FlightType | null {
    switch (pluginId) {
        case NovaPluginIds.Ortho:
            return "ortho"
        case NovaPluginIds.IR:
            return "infrared"
        default:
            return null
    }
}

function buildPathLabel(
    id: string,
    name: string,
    entityMap: Record<string, SidePanelLayerOrGroup>
): string {
    const ancestors = getAncestors(id, entityMap)
    if (ancestors.length === 0) return name
    return [...ancestors.reverse().map((a) => a.name), name].join(" / ")
}

// Mirrors the create-args field set the server-side import action validates against.
function toStagedEntry(img: OrthoImage | InfraredImage): StagedEntry {
    return {
        id: img.id,
        fileName: img.id,
        state: ImageProcessingState.Uploading,
        isRemoteImport: true,
        longitude: img.longitude,
        latitude: img.latitude,
        altitude: img.altitude,
        height: img.height,
        pitch: img.pitch,
        yaw: img.yaw,
        roll: img.roll,
        takenAt: img.takenAt,
        make: img.make,
        focalLength: img.focalLength,
        size: img.size,
        imageHeight: img.imageHeight,
        imageWidth: img.imageWidth,
        imageType: img.imageType,
        fov: img.fov,
    }
}

type StagedSource = {
    source: SourceFlight
    newImages: (OrthoImage | InfraredImage)[]
}

// Stages before requesting the import: mergeImagesAndUploads reads an arriving image with
// no matching upload entry as already settled, so staging any later risks losing the race
// to a fast backend chunk.
function stageSourceImages(
    dispatch: AppDispatch,
    source: SourceFlight,
    targetFlightId: string,
    targetType: FlightType,
    existingIds: Set<string>
): (OrthoImage | InfraredImage)[] {
    const newImages = source.images.filter((img) => !existingIds.has(img.id))
    newImages.forEach((img) => existingIds.add(img.id))

    if (newImages.length > 0) {
        const entries = newImages.map(toStagedEntry)
        if (targetType === "ortho") {
            dispatch(stageOrthoRemote({ setID: targetFlightId, entries: entries as OrthoState[] }))
        } else {
            dispatch(
                stageInfraredRemote({
                    setID: targetFlightId,
                    entries: entries as InfraredImageState[],
                })
            )
        }
    }
    return newImages
}

function markImagesErrored(
    dispatch: AppDispatch,
    images: (OrthoImage | InfraredImage)[],
    targetType: FlightType,
    message: string
) {
    if (images.length === 0) return
    const errored = images.map((img) => ({
        ...toStagedEntry(img),
        error: { name: "ImportFailed", message: "Import failed" },
    }))
    if (targetType === "ortho") {
        dispatch(updateManyOrthoUploads(errored as never as OrthoState[]))
    } else {
        dispatch(updateManyInfraredUploads(errored as never as InfraredImageState[]))
    }
    dispatch(
        setGlobalNotificationContext({
            message,
            status: GlobalNotificationStatus.error,
            timeoutInMs: 7000,
        })
    )
}

// Watches for the overall import job's success/failure ack (see import-images.ts's
// postIngestStatus), keyed by the requestId the trigger request returned.
function watchImportOutcome(
    dispatch: AppDispatch,
    requestId: string,
    newImages: (OrthoImage | InfraredImage)[],
    source: SourceFlight,
    targetType: FlightType
) {
    ingestEventEmitter.once(requestId, (response: IngestResponse) => {
        if (response.status !== "success") {
            markImagesErrored(
                dispatch,
                newImages,
                targetType,
                `Failed to import images from ${source.pathLabel}. Try again.`
            )
        }
    })
}

// Reads/writes only redux state (not component state) so it survives the
// AddFiles → PreviewFiles unmount that loses anything tracked in handleImport's component state.
export function useImportArrivalWatcher() {
    const dispatch = useAppDispatch()
    const { pluginId, flightId } = useUploadFlowContext()
    const targetType = loadFromLayerTargetType(pluginId)

    const orthoUploads = useAppSelector(selectOrthoUploadsForSet(flightId))
    const infraredUploads = useAppSelector(selectInfraredUploadsForSet(flightId))
    const targetOrthoFlight = useAppSelector(selectOrthoFlightById(flightId))
    const targetIrFlight = useAppSelector(selectInfraredFlightById(flightId))

    useEffect(() => {
        if (targetType === "ortho") {
            const pending = (orthoUploads ?? [])
                .map((u) => isUsable(u) as OrthoState | undefined)
                .filter(
                    (u): u is OrthoState =>
                        !!u &&
                        u.isRemoteImport === true &&
                        u.state !== ImageProcessingState.IngestDone
                )
            const arrivedIds = new Set(targetOrthoFlight?.images.map((img) => img.id) ?? [])
            const completed = pending.filter((u) => arrivedIds.has(u.id))
            if (completed.length > 0) {
                dispatch(
                    updateManyOrthoUploads(
                        completed.map((u) => ({ ...u, state: ImageProcessingState.IngestDone }))
                    )
                )
            }
        } else if (targetType === "infrared") {
            const pending = (infraredUploads ?? [])
                .map((u) => isUsable(u) as InfraredImageState | undefined)
                .filter(
                    (u): u is InfraredImageState =>
                        !!u &&
                        u.isRemoteImport === true &&
                        u.state !== ImageProcessingState.IngestDone
                )
            const arrivedIds = new Set(targetIrFlight?.images.map((img) => img.id) ?? [])
            const completed = pending.filter((u) => arrivedIds.has(u.id))
            if (completed.length > 0) {
                dispatch(
                    updateManyInfraredUploads(
                        completed.map((u) => ({ ...u, state: ImageProcessingState.IngestDone }))
                    )
                )
            }
        }
    }, [targetType, orthoUploads, infraredUploads, targetOrthoFlight, targetIrFlight, dispatch])
}

/**
 * Trigger button + modal for copying images out of an existing layer into the
 * current upload flow. Owns its own visibility state (no modal registry).
 * Renders nothing for plugins that don't support image loading.
 */
export const LoadFromLayerButton = ({ className }: { className?: string }) => {
    const { pluginId } = useUploadFlowContext()
    const { isEditor } = useWorkspacePermissions()
    const [showModal, setShowModal] = useState(false)
    const targetType = loadFromLayerTargetType(pluginId)

    if (!isEditor || !targetType) return null

    return (
        <>
            <ButtonSecondary
                title="Load from Layer"
                LeftIcon={Icons.Download}
                className={classNames("w-full", className)}
                onClick={() => setShowModal(true)}
            />
            {showModal && <LoadFromLayerModal onClose={() => setShowModal(false)} />}
        </>
    )
}

const LoadFromLayerModal = ({ onClose }: { onClose: () => void }) => {
    const dispatch = useAppDispatch()
    const { portfolioId, fireId, flightId: targetFlightId, pluginId } = useUploadFlowContext()
    const targetType = loadFromLayerTargetType(pluginId)

    const [selected, setSelected] = useState<SourceFlight[]>([])
    const [search, setSearch] = useState("")

    const entityMap = useAppSelector(selectAllEntitiesMap)
    const sortMode = useAppSelector(selectSortLayersValue)
    const orthoFlights = useAppSelector(selectNonArchivedOrthoFlights)
    const irFlights = useAppSelector(selectNonArchivedInfraredFlights)
    const targetOrthoFlight = useAppSelector(selectOrthoFlightById(targetFlightId))
    const targetIrFlight = useAppSelector(selectInfraredFlightById(targetFlightId))

    const compareFn = compareFunctionsBySortMode[sortMode]

    const allFlights: SourceFlight[] = [
        ...orthoFlights
            .filter((f) => f.id !== targetFlightId && f.images.length > 0)
            .map((f: OrthoFlight): OrthoSourceFlight => ({
                id: f.id,
                pathLabel: buildPathLabel(f.id, f.name, entityMap),
                type: "ortho",
                imageCount: f.images.length,
                estimatedBytes: f.images.reduce((sum, img) => sum + (img.size ?? 0), 0),
                images: f.images,
            })),
        ...irFlights
            .filter((f) => f.id !== targetFlightId && f.images.length > 0)
            .map((f: InfraredFlight): InfraredSourceFlight => ({
                id: f.id,
                pathLabel: buildPathLabel(f.id, f.name, entityMap),
                type: "infrared",
                imageCount: f.images.length,
                estimatedBytes: f.images.reduce((sum, img) => sum + (img.size ?? 0), 0),
                images: f.images,
            })),
    ].sort((a, b) => {
        const ea = entityMap[a.id]
        const eb = entityMap[b.id]
        if (!ea || !eb) return 0
        return compareFn(ea, eb)
    })

    const visibleFlights = search
        ? allFlights.filter((f) => f.pathLabel.toLowerCase().includes(search.toLowerCase()))
        : allFlights

    const isCrossType =
        selected.length > 0 && targetType !== null && selected.some((s) => s.type !== targetType)

    const handleImport = () => {
        if (selected.length === 0 || !portfolioId || !fireId || !targetType) return

        const targetFlight = targetType === "ortho" ? targetOrthoFlight : targetIrFlight
        const existingIds = new Set(targetFlight?.images.map((img) => img.id) ?? [])
        const staged: StagedSource[] = selected.map((source) => ({
            source,
            newImages: stageSourceImages(dispatch, source, targetFlightId, targetType, existingIds),
        }))
        onClose()

        void Promise.allSettled(
            staged.map(async ({ source, newImages }) => {
                const response = await fetchApiGateway(
                    "post /org/:orgId/project/:projectId/flight/:flightId/import-images",
                    {
                        params: {
                            orgId: portfolioId,
                            projectId: fireId,
                            flightId: targetFlightId,
                        },
                        body: {
                            sourceFlightId: source.id,
                            sourceFlightType: source.type,
                            targetFlightType: targetType,
                        },
                    }
                )
                watchImportOutcome(dispatch, response.requestId, newImages, source, targetType)
            })
        ).then((results) => {
            results.forEach((result, i) => {
                if (result.status !== "rejected") return
                const { source, newImages } = staged[i]
                const message =
                    result.reason instanceof Error && result.reason.message.includes("402")
                        ? `Not enough storage capacity to import ${source.pathLabel}.`
                        : `Failed to import images from ${source.pathLabel}. Try again.`
                markImagesErrored(dispatch, newImages, targetType, message)
            })
        })
    }

    return (
        <HighPriorityNovaModalContainer
            title="Load from Layers"
            icon={Icons.Download}
            closeModal={onClose}
            className="w-[28rem] max-h-[80vh]"
            bottomBar={
                <div className="flex w-full items-center justify-between gap-3">
                    {/* Reserved-height usage line keeps the CTA from shifting. */}
                    <small className="text-gray-300 min-h-[1rem]">
                        {selected.length > 0
                            ? (() => {
                                  const total = selected.reduce(
                                      (sum, s) => sum + s.estimatedBytes,
                                      0
                                  )
                                  return total > 0
                                      ? `Adds ~${formatBytes(total)} to your usage`
                                      : "Storage size not available"
                              })()
                            : ""}
                    </small>
                    <ButtonPrimary
                        title={isCrossType ? "Load Anyway" : "Load Images"}
                        onClick={handleImport}
                        disabled={selected.length === 0}
                    />
                </div>
            }
        >
            <div className="flex flex-col gap-2 min-h-0 pt-1">
                {allFlights.length === 0 ? (
                    <small className="text-gray-400">
                        No layers with images found in this project.
                    </small>
                ) : (
                    <>
                        {allFlights.length > 5 && (
                            <WorkspaceCompactTextInput
                                value={search}
                                handleChange={setSearch}
                                placeholder="Search layers"
                                name="load-from-layer-search"
                            />
                        )}
                        <div className="flex flex-col overflow-y-auto max-h-[45vh] pr-0.5">
                            {visibleFlights.length === 0 ? (
                                <small className="text-gray-400">
                                    No layers match your search.
                                </small>
                            ) : (
                                visibleFlights.map((flight) => (
                                    <FlightRow
                                        key={flight.id}
                                        flight={flight}
                                        isSelected={selected.some((s) => s.id === flight.id)}
                                        onSelect={() =>
                                            setSelected((prev) =>
                                                prev.some((s) => s.id === flight.id)
                                                    ? prev.filter((s) => s.id !== flight.id)
                                                    : [...prev, flight]
                                            )
                                        }
                                    />
                                ))
                            )}
                        </div>
                    </>
                )}

                {isCrossType && targetType && (
                    <div className="bg-yellow-900/40 border border-yellow-600/50 rounded-md px-3 py-2 flex gap-2">
                        <Icons.SmallIcon
                            Icon={Icons.Warning}
                            className="text-yellow-400 mt-0.5 shrink-0"
                        />
                        <small className="text-yellow-200">
                            {(() => {
                                const crossTypes = [
                                    ...new Set(
                                        selected
                                            .filter((s) => s.type !== targetType)
                                            .map((s) => s.type)
                                    ),
                                ]
                                return crossTypes.length === 1 ? (
                                    <>
                                        <strong>{FLIGHT_TYPE_LABEL[crossTypes[0]]}</strong> images
                                        into a <strong>{FLIGHT_TYPE_LABEL[targetType]}</strong>{" "}
                                        layer - results may be unexpected.
                                    </>
                                ) : (
                                    <>
                                        Some selected layers have mismatched image types — results
                                        may be unexpected.
                                    </>
                                )
                            })()}
                        </small>
                    </div>
                )}
            </div>
        </HighPriorityNovaModalContainer>
    )
}

const FlightRow = ({
    flight,
    isSelected,
    onSelect,
}: {
    flight: SourceFlight
    isSelected: boolean
    onSelect: () => void
}) => (
    <div
        className={classNames(
            "flex gap-2 w-full px-2 py-[6px] items-center rounded-md transition-colors cursor-pointer group",
            isSelected ? "bg-gray-600" : "hover:bg-gray-700"
        )}
        onClick={onSelect}
    >
        <div className="w-5 h-5 flex justify-center items-center">
            <Icons.SmallIcon
                className="shrink-0"
                Icon={FLIGHT_TYPE_ICON[flight.type]}
                iconClassName={isSelected ? "text-white-600" : "text-white-400"}
            />
        </div>
        <span className="text-[12px] text-gray-50 truncate flex-1 select-none">
            {flight.pathLabel}
        </span>
        <small className="text-[12px] text-gray-300 shrink-0">
            {flight.imageCount} image{flight.imageCount !== 1 ? "s" : ""}
        </small>
        {isSelected && (
            <Icons.SmallIcon Icon={Icons.Checkmark} className="text-blue-400 shrink-0" />
        )}
    </div>
)
